// src/pages/FollowUpConsultNote.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc, getDoc, setDoc, addDoc, collection,
  serverTimestamp, query, where, orderBy, getDocs
} from "firebase/firestore";
import { db } from "../firebase";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { Save, ArrowLeft, CheckCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import SmartCaptureModal from "../components/SmartCaptureModal";
import { apiPost } from "../utils/api";  // ✅ versioned helper

const NoteSectionTextarea = ({ label, ...props }) => (
  <div>
    <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</label>
    <textarea
      {...props}
      className="w-full h-48 bg-slate-100 dark:bg-zinc-800 rounded-lg p-4 text-gray-900 dark:text-white border-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

export default function FollowUpConsultNote() {
  const { patientId, noteId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [showSmartCapture, setShowSmartCapture] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const [patientName, setPatientName] = useState("");
  const [activeTab, setActiveTab] = useState("Consult Q");

  const [noteData, setNoteData] = useState({
    consultQuestion: "",
    intervalHistory: "",
    exam: "",
    ap: "",
    followUp: "",
    type: "Follow-up Consult",
  });

  const noteTabs = ["Consult Q", "Interval Hx", "Exam", "A&P", "Follow-Up"];

  // ───────────────────────────────────────────
  // FETCH PATIENT NAME
  // ───────────────────────────────────────────
  const fetchPatientName = useCallback(async () => {
    const snap = await getDoc(doc(db, "patients", patientId));
    if (snap.exists()) {
      const d = snap.data();
      setPatientName(`${d.firstName || ""} ${d.lastName || ""}`.trim());
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatientName();

    if (noteId && noteId !== "new") {
      getDoc(doc(db, "notes", noteId)).then(snap => {
        if (snap.exists()) setNoteData(prev => ({ ...prev, ...snap.data() }));
      });
    }
  }, [noteId, patientId, fetchPatientName]);

  const handleChange = e => {
    setNoteData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ───────────────────────────────────────────
  // SAVE NOTE
  // ───────────────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    const summary = `Consult Question: ${noteData.consultQuestion}\nInterval History: ${noteData.intervalHistory}\nA&P: ${noteData.ap}`;

    const payload = { ...noteData, summary, timestamp: serverTimestamp() };

    if (noteId && noteId !== "new") {
      await setDoc(doc(db, "notes", noteId), payload, { merge: true });
    } else {
      const ref = await addDoc(collection(db, "notes"), {
        ...payload,
        ownerId: currentUser.uid,
        patientID: patientId,
      });
      navigate(`/follow-up-consult-note/${patientId}/${ref.id}`, { replace: true });
    }

    setIsSaved(true);
    setIsSaving(false);
    setTimeout(() => setIsSaved(false), 2000);
  };

  // ───────────────────────────────────────────
  // GENERATE A&P
  // ───────────────────────────────────────────
  const handleGenerateAP = async () => {
    setIsGenerating(true);

    try {
      const q = query(
        collection(db, "notes"),
        where("patientID", "==", patientId),
        where("type", "in", ["Initial Consult", "Follow-up Consult"]),
        orderBy("timestamp", "desc")
      );
      const snap = await getDocs(q);

      const past_notes_context = snap.docs
        .map(d => {
          const n = d.data();
          const ts = n.timestamp?.toDate?.().toLocaleDateString() || "N/A";
          return `--- Past Consult (${ts}) ---\n${n.summary || ""}`;
        })
        .join("\n\n");

      const current_note_context = `Consult Question: ${noteData.consultQuestion}\nInterval: ${noteData.intervalHistory}\nExam: ${noteData.exam}`;

      const payload = {
        context: "consult",
        consult_question: noteData.consultQuestion,
        current_note_context,
        past_notes_context,
      };

      const result = await apiPost("/generate-ap-v2", payload);  // ✅ versioned
      setNoteData(prev => ({ ...prev, ap: result.summary || prev.ap }));
    } catch (err) {
      setNoteData(prev => ({ ...prev, ap: `Error: ${err.message}` }));
    }

    setIsGenerating(false);
  };

  // ───────────────────────────────────────────
  // SMART CAPTURE → FORMAT CONSULT FOLLOWUP TEXT
  // ───────────────────────────────────────────
  const handleSmartCaptureSubmit = async (raw) => {
    if (raw.type === "audio") {
      // parent will send audio to /v1/transcribe
      const form = new FormData();
      form.append("file", raw.audioBlob, "audio.mp3");

      const res = await apiPost("/transcribe-consult", form, true); // third arg = multipart
      const text = res.text || "";
      raw = { type: "text", text };
    }

    if (raw.type === "image") {
      const res = await apiPost("/extract-text-from-image", {
        image: raw.imageBase64,
      });
      raw = { type: "text", text: res.text };
    }

    const formatted = await apiPost("/format-consult-followup-text", {
      text: raw.text,
    });

    setNoteData(prev => ({
      ...prev,
      consultQuestion: formatted.consult_question || prev.consultQuestion,
      intervalHistory: formatted.interval_history || prev.intervalHistory,
      exam: formatted.exam || prev.exam,
      ap: formatted.ap || prev.ap,
      followUp: formatted.follow_up || prev.followUp,
    }));
  };

  // ───────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────
  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl">

          {/* HEADER */}
          <div className="p-6 border-b border-gray-200 dark:border-zinc-800 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                  Follow-Up Consult Note
                </h1>
                <p className="text-gray-500 dark:text-gray-400">for {patientName}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSmartCapture(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
              >
                <Sparkles size={16} /> Smart Capture
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving || isSaved}
                className={`flex items-center gap-2 px-8 py-2 rounded-full text-white font-semibold transition ${
                  isSaved ? "bg-green-500" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {isSaving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"/> :
                 isSaved ? <><CheckCircle size={18}/> Saved!</> :
                 <><Save size={18}/> Save</>}
              </button>
            </div>
          </div>

          {/* TABS */}
          <div className="p-6">
            <div className="flex justify-center mb-8">
              <div className="flex items-center bg-slate-200/70 dark:bg-zinc-800 p-1 rounded-full space-x-2">
                {noteTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="relative px-5 py-1.5 text-sm font-semibold"
                  >
                    <span className={`relative z-10 ${
                      activeTab === tab ? "text-gray-900 dark:text-white" : "text-gray-500"
                    }`}>
                      {tab}
                    </span>
                    {activeTab === tab && (
                      <motion.div
                        layoutId="active-pill"
                        className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* TAB CONTENT */}
            {activeTab === "Consult Q" && (
              <NoteSectionTextarea name="consultQuestion" value={noteData.consultQuestion} label="Consult Question" onChange={handleChange}/>
            )}

            {activeTab === "Interval Hx" && (
              <NoteSectionTextarea name="intervalHistory" value={noteData.intervalHistory} label="Interval History" onChange={handleChange}/>
            )}

            {activeTab === "Exam" && (
              <NoteSectionTextarea name="exam" value={noteData.exam} label="Exam" onChange={handleChange}/>
            )}

            {activeTab === "A&P" && (
              <div className="space-y-4">
                <NoteSectionTextarea name="ap" value={noteData.ap} label="Assessment & Plan" onChange={handleChange}/>
                <button
                  onClick={handleGenerateAP}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full"
                >
                  {isGenerating ? <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin rounded-full" /> : <><Sparkles size={16}/> Generate A&P</>}
                </button>
              </div>
            )}

            {activeTab === "Follow-Up" && (
              <NoteSectionTextarea name="followUp" value={noteData.followUp} label="Follow-Up Recommendations" onChange={handleChange}/>
            )}

          </div>
        </div>
      </main>

      <SmartCaptureModal
        show={showSmartCapture}
        onClose={() => setShowSmartCapture(false)}
        onTextExtracted={handleSmartCaptureSubmit}
      />
    </div>
  );
}
